/**
 * Extend Number interface to include custom toFixed signature
 */
declare global {
	interface Number {
		toFixedSafe(fractionDigits?: number): string;
	}
}

// Store reference to native toFixed before overriding
const nativeToFixed = Number.prototype.toFixed;

/**
 * Initialize the custom toFixed method on Number prototype
 * Call this function once during app initialization
 *
 * This implementation fixes precision issues with the native toFixed method
 * by using exponential notation for accurate rounding.
 *
 * @example
 * (2.345).toFixedSafe(2) // Returns "2.35" instead of "2.34"
 */
export function initCustomToFixed(): void {
	if (typeof Number.prototype.toFixedSafe === 'undefined') {
		Number.prototype.toFixedSafe = function (fractionDigits?: number): string {
			// Default to 0 decimal places
			let digits = fractionDigits ?? 0;

			// Validate range (ECMAScript spec: 0-100)
			if (digits < 0 || digits > 100) {
				throw new RangeError('toFixed() digits argument must be between 0 and 100');
			}

			digits = Math.floor(digits);

			const number = Number(this);

			// Handle NaN
			if (isNaN(number)) {
				return 'NaN';
			}

			// Handle Infinity
			if (!isFinite(number)) {
				return number > 0 ? 'Infinity' : '-Infinity';
			}

			// For very large numbers, use native implementation to avoid precision loss
			if (Math.abs(number) >= 1e21) {
				return nativeToFixed.call(this, digits);
			}

			// Use exponential notation for accurate rounding
			// This fixes issues like (2.345).toFixedSafe(2) returning "2.34" instead of "2.35"
			let rounded: number;
			try {
				rounded = Number(Math.round(Number(`${number}e${digits}`)) + `e-${digits}`);
			} catch (e) {
				// Fallback to native if exponential notation fails
				return nativeToFixed.call(this, digits);
			}

			// Handle edge case: if rounding resulted in NaN or Infinity
			if (!isFinite(rounded)) {
				return nativeToFixed.call(this, digits);
			}

			// Convert to string
			let result = rounded.toString();

			// Handle cases where result is in exponential notation
			if (result.includes('e')) {
				// For very small or large numbers in exponential form, use native implementation
				return nativeToFixed.call(this, digits);
			}

			// Split into integer and decimal parts
			const parts = result.split('.');
			const integerPart = parts[0];
			let decimalPart = parts[1] || '';

			// Pad with zeros if needed
			while (decimalPart.length < digits) {
				decimalPart += '0';
			}

			// Return formatted result
			return digits > 0 ? `${integerPart}.${decimalPart}` : integerPart;
		};
	}
}
